import { Box, Button, createStyles, FormControl, Grid, InputLabel, makeStyles, MenuItem, Paper, Select, Theme, Typography, useTheme } from "@material-ui/core";
import React, { useEffect } from "react";
import { APIDeployment, Service, Version, EventDeployment } from "./Product.model";
import Table, { TableColumn } from "legacy-components/table/Table";
import CopyToClipboard from "legacy-components/form/action/CopyToClipboard";
import { ServiceVersionDetails } from "pages/home/cloud-services/ServiceVersionDetails";
import ModusIcon from "legacy-components/icons/ModusIcon";
import clsx from 'clsx';
import { useIdentityEnvironmentFilter } from "pages/home/subscriptions/components/identity-environment-filter/IdentityEnvironmentFilter";
import { ErrorMessage } from "modus/components/error-message/ErrorMessage";
import { ScopeList } from "./ScopeList";
import ManageAPIsDrawer from "./ManageAPIsDrawer";
import Authorizer from "modus/components/authorizer/Authorizer";
import { MY_TEAM_KEY } from "header/team-selector/TeamSelector";
import { useSelector } from "react-redux";
import {useAuthorization} from "../../authorizer/components/AuthorizationProvider";

export interface ProductSummaryViewModel {
    productId: string;
  productName: string;
  versions: Version[];
  services?: Service[];
}

const Actions = (props: { data: any }) => {
  return (
    <CopyToClipboard size="small" edge="end" text={props?.data?.endpoint} key={props?.data?.endpoint} className={`copy-to-clipboard ${props?.data?.endpoint}`}/>
  );
};

const RegionMap = {
    "us-west-1":  "US West",
    "us-west-2": "US West",
    "us-east-1": "US East",
    "us-east-2": "US East",
    "us": "US",
    "uk": "UK",
    "global": "Global",
    "au": "Australia",
    "ap": "Asia Pacific",
    "eu-west": "EU-West"
} as const;

type IRegionKey = keyof typeof RegionMap;

const Region = (props: { data: any }) => {
  return (
    <span>{RegionMap[props?.data?.geography as IRegionKey] ?? "Global"}</span>
  );
};


const useProductServicesViewerStyles = makeStyles((theme: Theme) =>
  createStyles({
    option: {
      padding: "12px 12px 11px 12px",
      borderBottom: `1px solid #CBCDD6`,
    },
    list: {
      padding: 0,
      maxHeight: `calc(60vh)`,
      overflowY: "auto",

    },
    expandMoreIcon: {
      color: `#464B52 !important`,
      marginRight: "15px",
      position: "absolute",
      right: "7px",
      pointerEvents: "none"
    },
    selectRoot: {
      paddingRight: "40px !important",
    },
    inputLabel: {
      transform: "translate(14px, -6px) scale(0.75)",
      pointerEvents: "none",
      backgroundColor: "#fff",
      padding: "0 4px",
      zIndex: 1,
      '&$focused': {
        color: theme.palette.primary.main,
      },
    },
    formControl: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      position: "relative",
      width: "100%",
      paddingRight: theme.spacing(2)
    },
    focused: {}
  }));

export interface ProductServicesViewerProps extends ProductSummaryViewModel {
  onUpdate?: () => void;
}

export const ProductServicesViewer: React.FC<ProductServicesViewerProps> = ({ productId, productName, versions, services, onUpdate }) => {
  const initialVersion = versions?.[0];
  const classes = useProductServicesViewerStyles();
  const [selectedDisplayService, setSelectedDisplayService] = React.useState<string>(initialVersion ? `${initialVersion.versionName}` : '');
  const [selectedVersion, setSelectedVersion] = React.useState<Version | undefined>(initialVersion);
  const [selectedService, setSelectedService] = React.useState<Service | undefined>(undefined);
  const [selectedType, setSelectedType] = React.useState<string>(initialVersion?.apiDeployments?.length ? "api" : "events");
  const [manageAPIsOpen, setManageAPIsOpen] = React.useState(false);
  const [apis, setApis] = React.useState<{ name: string; description: string; }[]>([]);
  const teamId = useSelector((state: any) =>state?.teamManager?.teams?.currentTeam?.id ?? localStorage.getItem(MY_TEAM_KEY));
  
  const onChange = (version: Version) => {
    setSelectedDisplayService(`${version.versionName}`);
    setSelectedVersion(version);
    setSelectedType(version.apiDeployments?.length ? "api" : "events");
  }

  const handleRemoveAPI = (name: string) => {
    setApis(apis.filter(api => api.name !== name));
  };
  const handleUpdateAPIs = () => {
    setManageAPIsOpen(false);
    if (onUpdate) onUpdate();
  };

    const { isActionAllowed } = useAuthorization("PRODUCT" , productId );
    const canUpdate = isActionAllowed("update");

  return (
    <Box>
      <Grid container alignItems="center">
          <Box paddingTop={2} paddingBottom={2}>
            {versions && versions?.length >= 1 && (
              <FormControl className={classes.formControl} variant="outlined">
                <Select 
                  IconComponent={(props) => (
                    <ModusIcon
                      name={'expand_more'}
                      {...props}
                      className={clsx([props.className, classes.expandMoreIcon])}
                    />
                  )}
                  MenuProps={{ classes: { list: classes.list } }}
                  classes={{ 
                    root: classes.selectRoot,
                    select: classes.selectRoot
                  }}
                  style={{
                    backgroundColor: '#fff', 
                    minWidth: '260px', 
                    maxWidth: '300px', 
                    height: '48px',
                  }} 
                  variant="outlined" 
                  value={selectedDisplayService}
                  labelId="api-version-select-label">
                  {versions?.map((version: Version) => {
                    return (
                      <MenuItem 
                        onClick={() => onChange(version)} 
                        value={`${version.versionName}`}
                        className={classes.option}
                        style={{fontSize: "14px", fontWeight: 400}}
                      >{version.versionName}</MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          </Box>
          <Box paddingTop={2} paddingBottom={2}>
          {(versions?.length >= 1 && 
          (selectedVersion?.apiDeployments && selectedVersion.apiDeployments?.length > 0 ||
          selectedVersion?.eventDeployments && selectedVersion.eventDeployments?.length > 0)
          ) && (
            <FormControl className={classes.formControl} variant="outlined">
              <Select
                fullWidth
                variant="outlined"
                value={selectedType}
                IconComponent={(props) =>  <ModusIcon
                  name={'expand_more'}
                  {...props}
                  className={clsx([props.className, classes.expandMoreIcon])}
                />}
                classes={{ root: classes.selectRoot }}
                MenuProps={{ classes: { list: classes.list } }}
                    style={{
                    backgroundColor: '#fff', 
                    minWidth: '100px', 
                    maxWidth: '142px', 
                    height: '48px',
                  }} 
                onChange={(e: React.ChangeEvent<{ value: unknown }>) => setSelectedType(e.target.value as string)}
                labelId="service-type-select-label"
              >
                {selectedVersion?.apiDeployments && selectedVersion.apiDeployments?.length > 0 && (<MenuItem className={classes.option} style={{fontSize: "14px", fontWeight: 400}} value={"api"}>APIs</MenuItem>)}
                {selectedVersion?.eventDeployments && selectedVersion.eventDeployments?.length > 0 && (<MenuItem className={classes.option} style={{fontSize: "14px", fontWeight: 400}}value={"events"}>Events</MenuItem>)}
              </Select>
            </FormControl>
          )}
          </Box>
          <Box paddingRight={2} paddingTop={3} paddingBottom={2}>
            {canUpdate && selectedVersion?.teamId === teamId &&  (
          <Authorizer resource="apis" action="create">
          <Button variant="outlined" color="primary" onClick={() => setManageAPIsOpen(true)} style={{ height: 48 }}>
            <Typography style={{ fontSize: "16px", fontWeight: 600 }}>
            Manage APIs
            </Typography>
          </Button>
          </Authorizer>) }
          </Box>
      </Grid>
      {selectedVersion && 
        ((selectedVersion?.apiDeployments && selectedVersion?.apiDeployments?.length > 0) || 
         (selectedVersion?.eventDeployments && selectedVersion?.eventDeployments?.length > 0)) && 
         <ServiceVersionDetails 
            productName={productName} 
            version={selectedVersion} 
            type={selectedType} 
            serviceName={selectedVersion?.serviceName || "default-service"}
         />}

      {selectedVersion && 
        ((!selectedVersion?.apiDeployments ||  selectedVersion?.apiDeployments?.length === 0) && 
         (!selectedVersion?.eventDeployments || selectedVersion?.eventDeployments?.length === 0)) && 
         <ErrorMessage message="No deployments available for the selected version"/>}
      <Authorizer resource="apis" action="create">
      <ManageAPIsDrawer
        open={manageAPIsOpen}
        onClose={() => setManageAPIsOpen(false)}
        productId={productName}
        onRemove={handleRemoveAPI}
        onUpdate={handleUpdateAPIs}
      />
      </Authorizer>
    </Box>
  );
};

interface IAPIServiceEndpoints {
  apiDeployments: APIDeployment[];
  serviceName: string;
  applications: any[];
}

interface IEventServiceEndpoints {
  eventDeployments: EventDeployment[]
}

export interface TransformedDeployment {
  endpoint?: string|null;
  geography?: string|null;
  isDeprecated?: boolean|undefined;
  provider?: string|null;
  type?: string|null;
  environment?: string|null;
}

export const APIServiceEndpoints = ({apiDeployments, applications, serviceName}:IAPIServiceEndpoints) => {

  const [deploymentList, setDeploymentList] = React.useState<TransformedDeployment[]>([]);
  useEffect(() => {
    const extractedDeploymentlist:TransformedDeployment[] = [];
    apiDeployments.forEach(deployment => {
      deployment.instances.forEach(instance => {
        extractedDeploymentlist.push({
          ...instance,
          environment: (deployment.environment ?? "N/A"),
        });
      });
    });

    setDeploymentList(extractedDeploymentlist);
  },[apiDeployments]);

  const {filterEndpointDeployments} = useIdentityEnvironmentFilter();

  return (
    <Paper style={{marginTop:"20px"}}>
      <Box p={2}>
        <Typography style={{fontSize:"14px", fontWeight: 'bold'}}>Service Endpoints</Typography>
        <Typography variant="body1" style={{fontSize:"14px", fontWeight: 'normal'}}>Please select the appropriate Service Endpoint needed for your Environment.</Typography>
      </Box>
      <Box p={2} style={{width:'100%'}}>
        <Table 
            rowData={filterEndpointDeployments(deploymentList)} 
            width='100%' height={"300px"} pagination={false}
            getRowNodeId={data => data.endpoint} 
            frameworkComponents={{Actions, Region}}>
          <TableColumn field="endpoint" flex={2} cellRenderer={"Link"}/>
           <TableColumn field="environment"  flex={1} />
          <TableColumn headerName="Region" flex={1} cellRenderer={"Region"}/>
          <TableColumn field="action"  width={100} cellRenderer={"Actions"}/>
        </Table>
      </Box>
      <Box p={2} style={{width:'100%'}}>
        {serviceName && (<ScopeList  applicationList={applications}/>)}
      </Box>
    </Paper>
  );
}

export const EventServiceEndpoints = ({eventDeployments}:IEventServiceEndpoints) => {
  const [deploymentList, setDeploymentList] = React.useState<TransformedDeployment[]>();

  useEffect(() => {
    const extractedDeploymentlist:TransformedDeployment[] = [];
    eventDeployments.forEach(deployment => {
      deployment.deployments.forEach(instance => {
        extractedDeploymentlist.push({
          ...instance,
          environment: (deployment.environment ?? "N/A"),
        });
      });
    });

    setDeploymentList(extractedDeploymentlist);
  },[eventDeployments]);

  return (
    <Paper style={{marginTop:"20px"}}>
      <Box p={2}>
        <Typography style={{fontSize:"14px", fontWeight: 'bold'}}>Event Endpoints</Typography>
        <Typography variant="body1" style={{fontSize:"14px", fontWeight: 'normal'}}>Please select the appropriate Event Endpoint needed for your Environment.</Typography>
      </Box>
      <Box p={2} style={{width:'100%'}}>
        <Table
          rowData={deploymentList}
          width='100%' height={"300px"} pagination={false}
          getRowNodeId={data => data.endpoint}
          frameworkComponents={{Actions}}>
          <TableColumn field="endpoint" flex={2} cellRenderer={"Link"}/>
          <TableColumn field="environment"  flex={1}/>
          <TableColumn field="protocol" headerName="Protocol" flex={1}/>
          <TableColumn field="action"  width={100} cellRenderer={"Actions"} />
        </Table>
      </Box>
    </Paper>
  );
}

export const ProductServices = ProductServicesViewer;